import React, { useEffect, useState } from 'react'
import './StorageForm.css'
import { useNavigate } from 'react-router-dom'
import { PageProperties } from '../../types'
import { logErrorToDb, pb } from '../../api-utils'
import { separator } from '../../App'
import { OAuthUser } from '../../site/pbTypes'
import { useLoginModal } from '../../contexts/LoginModalContext'
import { useSessionExpiredModal } from '../../contexts/SessionExpiredModalContext'
import {
  appendHelcimPayIframe,
  setupPaymentEventListener,
  PAYMENT_CONSTANTS
} from '../../payment-utils'
import Spinner from '../../components/Spinner'
import ErrorModal from '../../components/ErrorModal'

import { useStorageFormParams } from './StorageForm/hooks/useStorageFormParams'
import { useSpaceLoader } from './StorageForm/hooks/useSpaceLoader'
import { useSignedAgreementFlow } from './StorageForm/hooks/useSignedAgreementFlow'
import { usePaymentCompletion } from './StorageForm/hooks/usePaymentCompletion'

import InnerForm from './StorageForm/InnerForm'

const {
  EMOJI_SIGNATURE,
  PLEASE_WAIT
} = PAYMENT_CONSTANTS;


const StorageForm = ({ page }: PageProperties) => {
  const {
    spaceId,
    hasSigned,
    formSubmitted,
    invoiceNumber,
    contractId,
    paidCustomerId,
    paidAmount,
    transactionId,
    paidDate,
    payMethod
  } = useStorageFormParams()

  const { space, hasLoaded, error: spaceLoadError } = useSpaceLoader(spaceId)

  const {
    checkoutToken: signedFlowCheckoutToken,
    pageTitle: signedFlowPageTitle,
    pageSubtitle: signedFlowPageSubtitle,
    error: signedFlowError
  } = useSignedAgreementFlow(hasSigned, space, contractId)

  const {
    pageTitle: paymentPageTitle,
    pageSubtitle: paymentPageSubtitle,
    error: paymentError
  } = usePaymentCompletion(
    invoiceNumber, space, paidDate, contractId,
    paidCustomerId, transactionId, payMethod, paidAmount
  )

  const checkoutToken = signedFlowCheckoutToken

  const [localPageTitle, setPageTitle] = useState<string | undefined>()
  const [localPageSubtitle, setPageSubtitle] = useState<string | undefined>()

  const pageTitle = localPageTitle || paymentPageTitle || signedFlowPageTitle
  const pageSubtitle = localPageSubtitle || paymentPageSubtitle || signedFlowPageSubtitle

  const [oauthUser] = useState<OAuthUser | undefined>();
  const [isAuthenticated, setIsAuthenticated] = useState(pb.authStore.isValid);
  const [authenticatedCustomer, setAuthenticatedCustomer] = useState<any>(null);
  const { setIsLoginModalOpen } = useLoginModal();
  const { setIsSessionExpiredModalOpen } = useSessionExpiredModal();

  const [firstName, setFirstName] = useState<string | undefined>();
  const [signPageUrl, setSignPageUrl] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const error = spaceLoadError || signedFlowError || paymentError
    if (error) {
      setErrorMessage(error);
      setShowErrorModal(true);
      const source = spaceLoadError ? 'space_load' : signedFlowError ? 'signed_agreement_flow' : 'payment_completion'
      logErrorToDb({
        event_name: `hook_error_${source}`,
        error_message: error,
        error_details: { spaceId, contractId, invoiceNumber },
      })
    }
  }, [spaceLoadError, signedFlowError, paymentError, spaceId, contractId, invoiceNumber]);

  useEffect(() => {
    if (checkoutToken) {
      appendHelcimPayIframe(checkoutToken)
    }
  }, [checkoutToken])

  useEffect(() => {
    const loadAuthenticatedCustomer = async () => {
      if (pb.authStore.isValid && pb.authStore.record?.id) {
        try {
          const customerData = await pb.collection('customers').getOne(pb.authStore.record.id)
          setAuthenticatedCustomer(customerData)
          setIsAuthenticated(true)
        } catch (error: any) {
          console.error('Failed to load customer data:', error)
          if (error?.status === 401 || error?.status === 403) {
            pb.authStore.clear()
            setIsSessionExpiredModalOpen(true)
          }
          setIsAuthenticated(false)
          setAuthenticatedCustomer(null)
        }
      } else {
        setIsAuthenticated(false)
        setAuthenticatedCustomer(null)
      }
    }

    loadAuthenticatedCustomer()
    const unsubscribe = pb.authStore.onChange(() => {
      loadAuthenticatedCustomer()
    })
    return unsubscribe
  }, [])

  // Setup payment event listener
  useEffect(() => {
    if (checkoutToken) {
      const cleanup = setupPaymentEventListener(
        checkoutToken,
        (eventMessage) => {
          navigate(
            `/storage-form?id=${spaceId}&esio_contract_id=${contractId}&invoiceNumber=${eventMessage.invoiceNumber}&customerCode=${eventMessage.customerCode}&amount=${eventMessage.amount}&transactionId=${eventMessage.transactionId}&paidDate=${eventMessage.dateCreated}&payMethod=${eventMessage.cardHolderName ? "cc" : "ach"}`
          );
        },
        (eventMessage) => { console.error('Payment aborted:', eventMessage) },
        (eventMessage) => { console.log('Payment cancelled:', eventMessage) }
      );
      return cleanup;
    }
  }, [checkoutToken, spaceId, contractId, navigate]);

  if (!hasLoaded) {
    return (<div className="view column forceBlack"
      style={{
        height: "70vh", padding: "8%",
        background: `linear-gradient(${page.gradient?.direction}, ${page.gradient?.first}, ${page.gradient?.second})`
      }}>
      <div className="forceBlack subtitle">...loading storage form</div>
      <Spinner />
    </div>)
  }
  else if (!space || space?.status === "rented") {
    return (
      <div className="view column forceBlack"
        style={{
          fontSize: "2rem", height: "70vh", padding: "8%",
          display: "flex", justifyContent: "center", alignItems: "center",
          background: `linear-gradient(${page.gradient?.direction}, ${page.gradient?.first}, ${page.gradient?.second})`
        }}>
        <div style={{ textAlign: 'center' }}>
          This space is no longer available. Please return to the Reservation page.<br />
          <button
            style={{
              marginTop: 32, fontSize: '1.2rem', padding: '0.6em 2em',
              background: '#232043', color: 'yellow', border: 'none',
              borderRadius: 4, cursor: 'pointer', fontWeight: 700, letterSpacing: 1.2
            }}
            onClick={() => window.location.href = '/reserve'}
          >
            Back
          </button>
        </div>
      </div>
    )
  } else {
    return (
      <div className="view column"
        style={{
          padding: checkoutToken && !invoiceNumber ? "8% 8% 2rem 8%" : "8%",
          minHeight: checkoutToken && !invoiceNumber ? "calc(100vh - 420px)" : "auto",
          background: `linear-gradient(${page.gradient?.direction}, ${page.gradient?.first}, ${page.gradient?.second})`
        }}>
        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          onClose={() => { setShowErrorModal(false); setErrorMessage(undefined); }}
        />

        {hasLoaded && (
          <div className={`title boldFontFamily ${page.textColor ? "" : "forceWhite"}`}>
            {signPageUrl
              ? (<div>
                  <span className="huge-icon" role="img" aria-label="img">{EMOJI_SIGNATURE}</span>
                  {`Thanks ${firstName}, please sign your storage agreement`}
                </div>)
              : pageTitle || (!formSubmitted && !hasSigned && !invoiceNumber && page.title) || PLEASE_WAIT
            }
          </div>)
        }
        {hasLoaded && (
          <div className="forceBlack subtitle">
            {signPageUrl
              ? <span style={{ paddingLeft: 4 }}>and check your email for the verification code required to sign</span>
              : pageSubtitle || (!formSubmitted && !hasSigned && !invoiceNumber && page.subtitle)}
            {signPageUrl && <>
              <script src="https://code.jquery.com/jquery-3.4.1.min.js"></script>
              <script src="https://cdn.jsdelivr.net/npm/iframe-resizer@4.2.10/js/iframeResizer.min.js"></script>
              <iframe
                src={`${signPageUrl}?embedded=yes`}
                id="eSignaturesIframe"
                style={{ margin: '1rem', minWidth: '85%', minHeight: '700px', border: 'none' }}>
              </iframe>
            </>}
            {checkoutToken && <script type="text/javascript" src="helcim-start.js"></script> && !invoiceNumber && (
              <>
                <br />
                <button type="button" style={{ background: '#232043', color: 'yellow' }}
                  onClick={() => appendHelcimPayIframe(checkoutToken)}>
                  <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Pay Now</span>
                </button>
              </>
            )}
            {separator}
          </div>
        )}
        {hasLoaded && !formSubmitted && !invoiceNumber && !hasSigned && (
          <InnerForm
            space={space}
            spaceId={spaceId}
            oauthUser={oauthUser}
            authenticatedCustomer={authenticatedCustomer}
            isAuthenticated={isAuthenticated}
            onSetPageTitle={setPageTitle}
            onSetPageSubtitle={setPageSubtitle}
            onSetFirstName={setFirstName}
            onSetSignPageUrl={setSignPageUrl}
            onError={(msg) => { setErrorMessage(msg); setShowErrorModal(true); }}
            onLoginClick={() => setIsLoginModalOpen(true, { redirectOnSuccess: false })}
            onSessionExpired={() => setIsSessionExpiredModalOpen(true)}
            navigate={navigate}
          />
        )}
      </div>
    )
  }
}

export default StorageForm
